import React, { memo, useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import Icon from '../Icon';
import { TreeContext } from './Context';

// Custom comparison function for memo - only re-render when necessary
const arePropsEqual = (prevProps, nextProps) => {
    // Always re-render if node reference changes (structural change)
    if (prevProps.node !== nextProps.node) return false;
    // Always re-render if path changes
    if (prevProps.path !== nextProps.path) return false;
    return true;
};

const TreeNode = memo(({ node, path }) => {
    // ========================================
    // CONTEXT
    // ========================================
    const {
        activePath,
        canSelectParents,
        classes,
        customs,
        id,
        onSelectNode,
        onToggleNode,
        override,
        toggledPaths,
    } = useContext(TreeContext);

    // ========================================
    // DERIVED STATE
    // ========================================
    const isActive = activePath === path;
    const isToggled = toggledPaths.has(path);
    const hasChildren = node.children && node.children.length > 0;
    const isSelectable = (canSelectParents || !hasChildren) && !node.isDisabled;

    // ========================================
    // EVENT HANDLERS & CALLBACKS
    // ========================================
    const handleSelect = useCallback(() => {
        onSelectNode(node, path);
    }, [node, path, onSelectNode]);

    const handleToggle = useCallback(() => {
        onToggleNode(node, path, !isToggled);
    }, [node, path, isToggled, onToggleNode]);

    // ========================================
    // MEMOIZATION
    // ========================================
    const nodeClasses = useMemo(
        () =>
            classnames(classes.node, {
                [`HoiPoi__Tree__${id}__node-id-${node.id}`]: id && node.id,
                [classes.active]: isActive,
                [classes.empty]: !hasChildren,
                [classes.isDisabled]: node.isDisabled,
                [classes.isSelectable]: isSelectable,
                [classes.toggled]: isToggled,
            }),
        [
            classes.active,
            classes.empty,
            classes.isDisabled,
            classes.isSelectable,
            classes.node,
            classes.toggled,
            hasChildren,
            id,
            isActive,
            isSelectable,
            isToggled,
            node.id,
            node.isDisabled,
        ],
    );

    const nodeProps = useMemo(
        () => ({ classes, customs, node, override, isActive, isToggled }),
        [classes, customs, node, override, isActive, isToggled],
    );

    // ========================================
    // MEMOIZED ELEMENTS
    // ========================================
    const NodeIconElement = useMemo(
        () => (
            <div
                className={classes.nodeIcon}
                onClick={!node.isDisabled ? handleToggle : undefined}
                {...override.nodeIcon}
            >
                {customs.NodeIcon ? (
                    <customs.NodeIcon {...nodeProps} />
                ) : (
                    <Icon name="chevron" size="small" />
                )}
            </div>
        ),
        [classes.nodeIcon, customs, handleToggle, node.isDisabled, nodeProps, override.nodeIcon],
    );

    const NodeItemElement = useMemo(
        () => (
            <div
                className={classes.nodeItem}
                onClick={!node.isDisabled ? handleSelect : undefined}
                {...override.nodeItem}
            >
                {customs.NodeItem ? <customs.NodeItem {...nodeProps} /> : node.name}
            </div>
        ),
        [
            classes.nodeItem,
            customs,
            handleSelect,
            node.isDisabled,
            node.name,
            nodeProps,
            override.nodeItem,
        ],
    );

    const nodeContent = useMemo(() => {
        if (customs.Container) {
            return (
                <customs.Container
                    {...nodeProps}
                    NodeIcon={NodeIconElement}
                    NodeItem={NodeItemElement}
                    onClick={handleToggle}
                />
            );
        }

        if (customs.Node) {
            return (
                <customs.Node
                    {...nodeProps}
                    NodeIcon={NodeIconElement}
                    NodeItem={NodeItemElement}
                />
            );
        }

        return (
            <div className={nodeClasses} {...override.node}>
                {NodeIconElement}
                {NodeItemElement}
            </div>
        );
    }, [
        NodeIconElement,
        NodeItemElement,
        customs,
        handleToggle,
        nodeClasses,
        nodeProps,
        override.node,
    ]);

    // ========================================
    // RENDER
    // ========================================
    return (
        <li className={classes.nodeWrapper}>
            {nodeContent}
            {hasChildren && isToggled && (
                <ul className={classes.subtree}>
                    {node.children.map((child, index) => {
                        const childPath = `${path}-${index}`;
                        return (
                            <TreeNode key={child.id ?? childPath} node={child} path={childPath} />
                        );
                    })}
                </ul>
            )}
        </li>
    );
}, arePropsEqual);

TreeNode.displayName = 'TreeNode';

TreeNode.propTypes = {
    node: PropTypes.shape({
        children: PropTypes.array,
        id: PropTypes.any,
        isDisabled: PropTypes.bool,
        name: PropTypes.string,
    }).isRequired,
    path: PropTypes.string.isRequired,
};

export default TreeNode;
